import { lazy, Suspense, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useOutletContext,
} from 'react-router-dom';
import { ControlRegisterProvider } from '@/components/ui/control-register';
import { ErrorBoundary } from './ErrorBoundary';
import {
  RouteErrorFallback,
  RoutePendingBar,
  RouteSkeleton,
  ShellSkeleton,
  trackRouteChunk,
} from './layouts/route-fallbacks';
// Deep import for the guards, NOT the `@/features/auth` barrel. The router is
// in the entry chunk by definition, and the barrel re-exports LoginPage and
// SetupPage — so importing guards through it made both screens eager and the
// `lazy()` calls below decorative. Guards are the only auth module the route
// table itself needs.
import {
  RequireAuth,
  RequireBranch,
  RequireCollections,
  RequireOrganization,
  RequireOrganizationAccess,
  RequireOperationalCategories,
  RequireStockOperations,
  RequireOnlineStore,
  RequireReviews,
  RequirePlatformAdmin,
  RequireRestaurant,
  RequireSell,
  RequireShippingZones,
  RequireTerminal,
  RequireWarehouses,
} from '@/features/auth/guards';

/**
 * Root router — owned by the integration lead only.
 *
 * Route groups are lazy so POS and office stay in separate chunks
 * (01-architecture.md §3). Every V1 route now mounts its accepted feature
 * export (the V1-00 placeholder layer was removed once V1-08 landed).
 */

/*
  Every `lazy()` factory below hands its promise to `trackRouteChunk`, which is
  what lights RoutePendingBar. This table has no loaders, so the only wait a
  navigation has is the chunk download, and react-router's own pending state
  cannot see that one — the reasoning is in route-fallbacks.tsx. The import
  specifiers are untouched, so Vite's chunk graph is exactly what it was.
*/

// V1-02 (accepted): real auth feature routes replace the placeholders.
// Each screen module directly: going through the barrel would give /login and
// /setup one shared chunk containing both, so signing in downloads the terminal
// setup screen nobody on that route has asked for.
const Login = lazy(() =>
  trackRouteChunk(import('@/features/auth/LoginPage').then((m) => ({ default: m.LoginPage }))),
);
const MerchantSignup = lazy(() =>
  trackRouteChunk(
    import('@/features/auth/MerchantSignupPage').then((m) => ({
      default: m.MerchantSignupPage,
    })),
  ),
);
const MerchantApplication = lazy(() =>
  trackRouteChunk(
    import('@/features/auth/MerchantApplicationPage').then((m) => ({
      default: m.MerchantApplicationPage,
    })),
  ),
);
const AcceptInvitation = lazy(() =>
  trackRouteChunk(
    import('@/features/auth/AcceptInvitationPage').then((m) => ({
      default: m.AcceptInvitationPage,
    })),
  ),
);
const BillingRequired = lazy(() =>
  trackRouteChunk(
    import('@/features/billing/BillingRequiredPage').then((m) => ({
      default: m.BillingRequiredPage,
    })),
  ),
);
const Setup = lazy(() =>
  trackRouteChunk(import('@/features/auth/SetupPage').then((m) => ({ default: m.SetupPage }))),
);
// V1-04/V1-05 (accepted): lead route compositions replace the placeholders.
const Register = lazy(() =>
  trackRouteChunk(import('./pos-routes').then((m) => ({ default: m.RegisterRoute }))),
);
const Queue = lazy(() =>
  trackRouteChunk(import('./pos-routes').then((m) => ({ default: m.QueueRoute }))),
);
const Tables = lazy(() =>
  trackRouteChunk(import('./pos-routes').then((m) => ({ default: m.TablesRoute }))),
);
const OrderEdit = lazy(() =>
  trackRouteChunk(import('./pos-routes').then((m) => ({ default: m.OrderEditRoute }))),
);
const RetailOrderDetail = lazy(() =>
  trackRouteChunk(import('@/features/pos/order-detail/RetailOrderWorkspace')),
);
// V1-06 (accepted): real operations screens replace the placeholders.
const Menu = lazy(() =>
  trackRouteChunk(import('@/features/pos/menu').then((m) => ({ default: m.MenuPage }))),
);
const Today = lazy(() =>
  trackRouteChunk(import('@/features/pos/today').then((m) => ({ default: m.TodayPage }))),
);
const Device = lazy(() =>
  trackRouteChunk(import('@/features/pos/device').then((m) => ({ default: m.DevicePage }))),
);
const Stock = lazy(() =>
  trackRouteChunk(import('@/features/pos/stock').then((m) => ({ default: m.StockPage }))),
);
// V1-07 (accepted): real office core screens replace the placeholders.
const OfficeHome = lazy(() =>
  trackRouteChunk(import('@/features/office/home').then((m) => ({ default: m.OfficeHomePage }))),
);
const OfficeAssistant = lazy(() =>
  trackRouteChunk(import('@/features/office/assistant/AssistantPage')),
);
const OfficeOnlineStore = lazy(() =>
  trackRouteChunk(
    import('@/features/office/online-store').then((m) => ({ default: m.OnlineStorePage })),
  ),
);
const OfficeCheckoutHistory = lazy(() => import('@/features/office/orders/CheckoutHistoryPage').then((m) => ({ default: m.CheckoutHistoryPage })));
const OfficeOrders = lazy(() =>
  trackRouteChunk(
    import('@/features/office/orders').then((m) => ({ default: m.OfficeOrdersPage })),
  ),
);
const OfficeOrderDetail = lazy(() =>
  trackRouteChunk(import('@/features/office/orders').then((m) => ({ default: m.OrderDetailPage }))),
);
const OfficeOrderCreate = lazy(() =>
  import('@/features/office/orders').then((module) => ({ default: module.OrderCreatePage })),
);
const OfficeOrderEdit = lazy(() =>
  trackRouteChunk(import('@/features/office/orders').then((m) => ({ default: m.OrderEditPage }))),
);
const OfficeProducts = lazy(() =>
  trackRouteChunk(
    import('@/features/office/products').then((m) => ({ default: m.OfficeProductsPage })),
  ),
);
const OfficeReviews = lazy(() =>
  trackRouteChunk(import('@/features/office/reviews').then((m) => ({ default: m.ReviewsPage }))),
);
const OfficeReviewDetail = lazy(() =>
  trackRouteChunk(
    import('@/features/office/reviews').then((m) => ({ default: m.ReviewDetailPage })),
  ),
);
const OfficeCollections = lazy(() =>
  trackRouteChunk(
    import('@/features/office/collections').then((m) => ({ default: m.CollectionsPage })),
  ),
);
const OfficeCollectionEditor = lazy(() =>
  trackRouteChunk(
    import('@/features/office/collections').then((m) => ({ default: m.CollectionEditorPage })),
  ),
);
const OfficeProductEditor = lazy(() =>
  trackRouteChunk(
    import('@/features/office/products').then((m) => ({ default: m.ProductEditorPage })),
  ),
);
const OfficeTaxonomy = lazy(() =>
  trackRouteChunk(import('@/features/office/taxonomy').then((m) => ({ default: m.TaxonomyPage }))),
);
const OfficeMedia = lazy(() =>
  import('@/features/office/media').then((m) => ({ default: m.MediaLibraryPage })),
);
const OfficeBlogs = lazy(() =>
  trackRouteChunk(import('@/features/office/blogs').then((m) => ({ default: m.BlogsPage }))),
);
const OfficeBlogEditor = lazy(() =>
  trackRouteChunk(import('@/features/office/blogs').then((m) => ({ default: m.BlogEditorPage }))),
);
const OfficePages = lazy(() =>
  trackRouteChunk(import('@/features/office/pages').then((m) => ({ default: m.PagesPage }))),
);
const OfficeMenus = lazy(() => import('@/features/office/menus/MenusPage').then((m) => ({ default: m.MenusPage })));
const OfficeMenuEditor = lazy(() => import('@/features/office/menus/MenuEditorPage').then((m) => ({ default: m.MenuEditorPage })));
const OfficePageEditor = lazy(() =>
  trackRouteChunk(import('@/features/office/pages').then((m) => ({ default: m.PageEditorPage }))),
);
// V1-08 (accepted): real office-B screens replace the placeholders.
const OfficeCustomers = lazy(() =>
  trackRouteChunk(
    import('@/features/office/customers').then((m) => ({ default: m.OfficeCustomersPage })),
  ),
);
const OfficeCustomerDetail = lazy(() =>
  trackRouteChunk(
    import('@/features/office/customers/CustomerDetailPage').then((m) => ({
      default: m.CustomerDetailPage,
    })),
  ),
);
const OfficeSubscribers = lazy(() =>
  trackRouteChunk(
    import('@/features/office/subscribers/SubscribersPage').then((m) => ({ default: m.SubscribersPage })),
  ),
);
const OfficeSegments = lazy(() =>
  trackRouteChunk(
    import('@/features/office/segments/SegmentsPage').then((m) => ({ default: m.SegmentsPage })),
  ),
);
const OfficeForms = lazy(() =>
  trackRouteChunk(
    import('@/features/office/forms/FormsPage').then((m) => ({ default: m.FormsPage })),
  ),
);
// Restaurant fast-follow follow-up: the floor plan the RestaurantTableSeeder
// stood in for. Registered for every organization type — the page itself
// handles a role that may not manage tables, and the office nav entry only
// appears for restaurants, so an ecommerce org never links here.
const OfficeFloorPlan = lazy(() =>
  trackRouteChunk(
    import('@/features/office/tables').then((m) => ({ default: m.OfficeFloorPlanPage })),
  ),
);
// V2 operations parity. Each is its own lazy chunk so the office bundle only
// pays for the screen actually opened.
const OfficeShipping = lazy(() =>
  trackRouteChunk(
    import('@/features/office/shipping').then((m) => ({ default: m.OfficeShippingPage })),
  ),
);
const OfficeShippingZoneEditor = lazy(() =>
  trackRouteChunk(
    import('@/features/office/shipping').then((m) => ({ default: m.ShippingZoneEditorPage })),
  ),
);
const OfficeAreas = lazy(() =>
  trackRouteChunk(import('@/features/office/areas').then((m) => ({ default: m.OfficeAreasPage }))),
);
const OfficePromotions = lazy(() =>
  trackRouteChunk(
    import('@/features/office/promotions').then((m) => ({ default: m.PromotionsPage })),
  ),
);
const OfficePromotionEditor = lazy(() =>
  trackRouteChunk(
    import('@/features/office/promotions').then((m) => ({ default: m.PromotionEditorPage })),
  ),
);
const OfficePaymentMethods = lazy(() =>
  trackRouteChunk(
    import('@/features/office/payments').then((m) => ({ default: m.OfficePaymentMethodsPage })),
  ),
);
const OfficeWarehouses = lazy(() =>
  trackRouteChunk(
    import('@/features/office/warehouses').then((m) => ({ default: m.WarehousesPage })),
  ),
);
const OfficeInventory = lazy(() =>
  trackRouteChunk(
    import('@/features/office/inventory').then((m) => ({ default: m.OfficeInventoryPage })),
  ),
);
const OfficePurchaseOrders = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing').then((m) => ({ default: m.PurchaseOrdersPage })),
  ),
);
const OfficeSuppliers = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing').then((m) => ({ default: m.SuppliersPage })),
  ),
);
const OfficePurchaseOrderCreate = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing/PurchaseOrderCreatePage').then((m) => ({
      default: m.PurchaseOrderCreatePage,
    })),
  ),
);
const OfficeTransfers = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing/TransferPages').then((m) => ({
      default: m.TransfersPage,
    })),
  ),
);
const OfficeTransferCreate = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing/TransferCreatePage').then((m) => ({
      default: m.TransferCreatePage,
    })),
  ),
);
const OfficeTransferDetail = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing/TransferPages').then((m) => ({
      default: m.TransferDetailPage,
    })),
  ),
);
const OfficeReceipts = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing').then((m) => ({ default: m.ReceiptsPage })),
  ),
);
const OfficeInventoryCounts = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing').then((m) => ({ default: m.InventoryCountsPage })),
  ),
);
const OfficeInventoryReturns = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing').then((m) => ({ default: m.InventoryReturnsPage })),
  ),
);
const OfficeInventoryInsights = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing').then((m) => ({ default: m.InventoryInsightsPage })),
  ),
);
const OfficePurchaseOrderDetail = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing').then((m) => ({ default: m.PurchaseOrderDetailPage })),
  ),
);
const OfficeSupplierDetail = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing').then((m) => ({ default: m.SupplierDetailPage })),
  ),
);
const OfficeRepairs = lazy(() =>
  trackRouteChunk(import('@/features/office/purchasing').then((m) => ({ default: m.RepairsPage }))),
);
const OfficeStockMovements = lazy(() =>
  trackRouteChunk(
    import('@/features/office/purchasing').then((m) => ({ default: m.StockMovementsPage })),
  ),
);
const OfficeCheckoutSettings = lazy(() => trackRouteChunk(import('@/features/office/store-settings/CheckoutSettingsPage').then((m) => ({ default: m.CheckoutSettingsPage }))));
const OfficeStoreSettings = lazy(() =>
  trackRouteChunk(
    import('@/features/office/store-settings').then((m) => ({ default: m.StoreSettingsPage })),
  ),
);
const OfficeBranchSettings = lazy(() =>
  trackRouteChunk(
    import('@/features/office/branch-settings').then((m) => ({
      default: m.OfficeBranchSettingsPage,
    })),
  ),
);
const OfficeAnalytics = lazy(() =>
  trackRouteChunk(
    import('@/features/office/analytics').then((m) => ({ default: m.OfficeAnalyticsPage })),
  ),
);
const OfficeSessions = lazy(() =>
  trackRouteChunk(
    import('@/features/office/sessions').then((m) => ({ default: m.OfficeSessionsPage })),
  ),
);
const OfficeSettings = lazy(() =>
  trackRouteChunk(
    import('@/features/office/settings').then((m) => ({ default: m.OfficeSettingsPage })),
  ),
);
const OfficeMenuHub = lazy(() =>
  trackRouteChunk(import('@/features/office/menu').then((m) => ({ default: m.OfficeMenuPage }))),
);
const InstalledApp = lazy(() =>
  trackRouteChunk(import('@/features/office/apps').then((m) => ({ default: m.InstalledAppPage }))),
);
const AppStore = lazy(() =>
  trackRouteChunk(import('@/features/office/apps').then((m) => ({ default: m.AppStorePage }))),
);
const AppDetails = lazy(() =>
  trackRouteChunk(import('@/features/office/apps').then((m) => ({ default: m.AppDetailsPage }))),
);
// Storefront themes. Two chunks, not one: the library page is a list, while the
// editor drags CodeMirror in behind it, and an owner checking which theme is
// live should not download an editor to do it.
const OfficeThemes = lazy(() =>
  trackRouteChunk(import('@/features/office/themes').then((m) => ({ default: m.ThemesPage }))),
);
// The visual editor is its own chunk off its own barrel: three panes, drag
// and drop and (lazily) CodeMirror, none of which the library page needs.
const OfficeThemeEditor = lazy(() =>
  trackRouteChunk(
    import('@/features/office/themes/editor').then((m) => ({ default: m.ThemeEditorPage })),
  ),
);
const OfficeThemeCode = lazy(() =>
  trackRouteChunk(
    import('@/features/office/themes').then((m) => ({ default: m.ThemeCodeEditorPage })),
  ),
);
// V1-01 (accepted): real shells + kit replace the bare boundaries/placeholder.
const PosShell = lazy(() =>
  trackRouteChunk(import('./pos-routes').then((m) => ({ default: m.PosShellRoute }))),
);
const OfficeShell = lazy(() =>
  trackRouteChunk(import('./pos-routes').then((m) => ({ default: m.OfficeShellRoute }))),
);
// Lazy like the shells beside it, and for the same reason: the router is in the
// entry chunk by definition, so a static import here would pull the settings
// sub-rail into it for every visitor including the ones who only ever see the
// login screen.
const SettingsShell = lazy(() =>
  trackRouteChunk(import('./layouts/SettingsShell').then((m) => ({ default: m.SettingsShell }))),
);
const PlatformShell = lazy(() =>
  trackRouteChunk(import('./layouts/PlatformShell').then((m) => ({ default: m.PlatformShell }))),
);
const PlatformDashboard = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/DashboardPage').then((m) => ({ default: m.PlatformDashboardPage })),
  ),
);
const PlatformStores = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/MerchantsPage').then((m) => ({ default: m.PlatformStoresPage })),
  ),
);
const PlatformAssistant = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/AssistantPage').then((m) => ({ default: m.PlatformAssistantPage })),
  ),
);
const PlatformUsers = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/UsersPage').then((m) => ({ default: m.PlatformUsersPage })),
  ),
);
const PlatformCreateStore = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/CreateStorePage').then((m) => ({
      default: m.PlatformCreateStorePage,
    })),
  ),
);
const PlatformStorefrontHealth = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/StorefrontHealthPage').then((m) => ({
      default: m.PlatformStorefrontHealthPage,
    })),
  ),
);
const PlatformStoreProvisioning = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/StoreProvisioningPage').then((m) => ({
      default: m.PlatformStoreProvisioningPage,
    })),
  ),
);
const PlatformPlans = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/PlansPage').then((m) => ({ default: m.PlatformPlansPage })),
  ),
);
const PlatformSubscriptions = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/SubscriptionsPage').then((m) => ({
      default: m.PlatformSubscriptionsPage,
    })),
  ),
);
const PlatformInvoices = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/InvoicesPage').then((m) => ({ default: m.PlatformInvoicesPage })),
  ),
);
const PlatformStoreApplications = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/StoreApplicationsPage').then((m) => ({
      default: m.PlatformStoreApplicationsPage,
    })),
  ),
);
const PlatformSettings = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/SettingsPage').then((m) => ({
      default: m.PlatformSettingsPage,
    })),
  ),
);
const PlatformAccount = lazy(() =>
  trackRouteChunk(
    import('@/features/platform/AccountPage').then((m) => ({ default: m.PlatformAccountPage })),
  ),
);
const Kit = lazy(() => trackRouteChunk(import('@/features/kit')));
const Health = lazy(() => trackRouteChunk(import('./HealthPage')));

/**
 * Last resort, one per route group, ABOVE the shell.
 *
 * It used to be the whole strategy: a single boundary wrapping the shell
 * element, so a render error in any one of the 25 office pages unmounted the
 * sidebar, the topbar, the org switcher and the branch switcher and left the
 * user with a reload button. It now catches only what genuinely leaves nothing
 * to keep — the shell itself, its chunk, or a guard. Page failures are caught
 * one level down by `RouteContent`'s errorElement.
 */
function GroupBoundary({
  children,
  /*
    THE FALLBACK IS A PARAMETER, because this boundary wraps two different
    kinds of thing and they are not the same shape.

    Around a PAGE, the shell is already mounted and drawing the rail and the
    content panel, so `RouteSkeleton` — a header band and a table card — lands
    exactly where the page will. Around a SHELL, nothing is mounted yet, and
    that same drawing was a full-window table card with no sidebar that then
    snapped into a completely different layout when the chunk arrived. See
    `ShellSkeleton`'s docblock.
  */
  fallback = <RouteSkeleton />,
}: {
  children?: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <ErrorBoundary>
      <Suspense fallback={fallback}>{children ?? <Outlet />}</Suspense>
    </ErrorBoundary>
  );
}

/**
 * The shell's content area as a pathless layout route — the second boundary,
 * mounted INSIDE the chrome.
 *
 * It carries the two states that must not take the shell with them:
 *
 *  - Suspense. Below the shell, so a cold /products paints the real sidebar and
 *    topbar with a page-shaped skeleton in the content area, instead of the one
 *    word that used to be the entire viewport.
 *  - `errorElement` (declared on the route object, since only react-router can
 *    supply it). React Router swaps the failing route's element for it, which
 *    means a page that throws replaces the content area and nothing else, and
 *    react-router clears the error on the next navigation — so the nav entries
 *    still on screen are a working way out.
 *
 * RoutePendingBar rides here too. It belongs to the shell rather than to the
 * route table, but this is the innermost place the router itself can mount it;
 * see the note in `route-fallbacks.tsx` for the handover.
 */
function RouteContent() {
  /*
    THE CONTEXT HAS TO BE FORWARDED, or this boundary silently eats it.

    `useOutlet` wraps its children in `OutletContext.Provider value={context}`
    unconditionally — so a BARE `<Outlet />` here does not merely fail to add
    anything, it overwrites whatever the shell published with `undefined`.
    OfficeShell hands the gated nav down for SettingsShell to read
    (OfficeOutletContext); without this line that read always missed and the
    settings rail fell back to re-deriving the gates itself, which is the bug
    the context was added to remove.
  */
  const context = useOutletContext();
  return (
    <>
      <RoutePendingBar />
      <Suspense fallback={<RouteSkeleton />}>
        <Outlet context={context} />
      </Suspense>
    </>
  );
}

function LegacyTransferRedirect() {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={{
        ...location,
        pathname: location.pathname.replace(/^\/inventory-transfers(?=\/|$)/, '/transfers'),
      }}
    />
  );
}

export const routes = [
  {
    path: '/signup',
    element: (
      <GroupBoundary>
        <MerchantSignup />
      </GroupBoundary>
    ),
  },
  {
    path: '/login',
    element: (
      <GroupBoundary>
        <Login />
      </GroupBoundary>
    ),
  },
  {
    path: '/accept-invitation',
    element: (
      <GroupBoundary>
        <AcceptInvitation />
      </GroupBoundary>
    ),
  },
  {
    path: '/setup',
    element: (
      <GroupBoundary>
        <Setup />
      </GroupBoundary>
    ),
  },
  {
    path: '/health',
    element: (
      <GroupBoundary>
        <Health />
      </GroupBoundary>
    ),
  },
  /*
    /kit is reachable on a deployed build now — the audit's "reach problem".

    It was gated on `import.meta.env.DEV` alone, which meant the one artefact
    that shows what the office is supposed to look like could only ever be
    opened by someone running Vite. That is a plausible cause of the drift this
    phase has been undoing: a designer or an owner asking "does this match the
    kit" had no kit to point at, so every review happened against a screenshot
    or against memory.

    Two conditions, and they do different jobs:

    · VITE_ENABLE_KIT — build-time, and OFF unless the string is exactly
      'true'. An unset variable leaves the route out of the table entirely, so
      a normal production build still has no /kit and Rollup still tree-shakes
      the gallery chunk out. Turning it on is one line in the deploy
      environment (`VITE_ENABLE_KIT=true`) and a rebuild; it cannot be flipped
      at runtime, which is the point.

    · RequireAuth — runtime, and the same gate every office route sits behind.
      An anonymous visitor to an enabled build is sent to /login exactly as
      they are for /office. Deliberately NOT RequireOrganization on top: a
      component gallery reads no tenant data, and requiring a nominated
      organization would bounce a signed-in designer to /setup.

    In DEV the wrapper is a bare Outlet, so a developer with no session keeps
    the open route they have today.
  */
  ...(import.meta.env.DEV || import.meta.env.VITE_ENABLE_KIT === 'true'
    ? [
        {
          path: '/kit',
          element: import.meta.env.DEV ? <Outlet /> : <RequireAuth />,
          children: [
            {
              index: true,
              element: (
                <GroupBoundary>
                  <Kit />
                </GroupBoundary>
              ),
            },
          ],
        },
      ]
    : []),
  {
    element: <RequireAuth />,
    children: [
      {
        path: '/signup/application',
        element: (
          <GroupBoundary>
            <MerchantApplication />
          </GroupBoundary>
        ),
      },
      {
        element: <RequirePlatformAdmin />,
        children: [
          {
            path: '/platform',
            element: (
              <GroupBoundary fallback={<ShellSkeleton />}>
                {/* The seated console's control register — see the office
                    shell below and src/components/ui/control-register.tsx. */}
                <ControlRegisterProvider value="desk">
                  <PlatformShell />
                </ControlRegisterProvider>
              </GroupBoundary>
            ),
            children: [
              {
                element: <RouteContent />,
                errorElement: <RouteErrorFallback />,
                children: [
                  { index: true, element: <PlatformDashboard /> },
                  { path: 'stores', element: <PlatformStores /> },
                  { path: 'assistant', element: <PlatformAssistant /> },
                  { path: 'stores/:organizationId/assistant', element: <PlatformAssistant /> },
                  { path: 'users', element: <PlatformUsers /> },
                  { path: 'stores/new', element: <PlatformCreateStore /> },
                  {
                    path: 'stores/:organizationId/storefront',
                    element: <PlatformStorefrontHealth />,
                  },
                  { path: 'plans', element: <PlatformPlans /> },
                  { path: 'subscriptions', element: <PlatformSubscriptions /> },
                  { path: 'invoices', element: <PlatformInvoices /> },
                  { path: 'store-applications', element: <PlatformStoreApplications /> },
                  {
                    path: 'store-applications/:applicationId',
                    element: <PlatformStoreProvisioning />,
                  },
                  { path: 'settings', element: <PlatformSettings /> },
                  { path: 'account', element: <PlatformAccount /> },
                  { path: '*', element: <Navigate to="/platform" replace /> },
                ],
              },
            ],
          },
        ],
      },
      {
        // V2-1: the ONLY thing every signed-in route shares is a tenant. What
        // each group needs beyond that is declared by the group, not by one
        // gate at the top — see src/features/auth/routing.ts.
        element: <RequireOrganization />,
        children: [
          {
            /*
              THE VISUAL THEME EDITOR IS FULL-BLEED (THEME-EDITOR-PLAN §7).

              A sibling of the office shell, not a child of it: the editor is
              three panes beside a storefront iframe that must be exactly the
              viewport's height, and the sidebar would only take width from
              the preview. It still sits under RequireOrganization and the
              store-lifecycle gate, in the desk register so its dialogs and
              menus match the office, and behind the same Owner-only check
              the code editor applies inside the page.
            */
            path: '/themes/:themeId/editor',
            element: (
              <GroupBoundary fallback={<ShellSkeleton />}>
                <ControlRegisterProvider value="desk">
                  <Outlet />
                </ControlRegisterProvider>
              </GroupBoundary>
            ),
            children: [
              {
                element: <RequireOrganizationAccess />,
                children: [
                  {
                    element: <RouteContent />,
                    errorElement: <RouteErrorFallback />,
                    children: [{ index: true, element: <OfficeThemeEditor /> }],
                  },
                ],
              },
            ],
          },
          {
            path: '/pos',
            element: (
              <GroupBoundary>
                <PosShell />
              </GroupBoundary>
            ),
            children: [
              {
                // The store lifecycle gate is INSIDE the shell. A suspended
                // store therefore keeps its navigation/account controls, but
                // replaces the entire route content before individual screens
                // can render one failed request per card.
                element: <RequireOrganizationAccess />,
                children: [
                  {
                    // Boundary + Suspense INSIDE the shell: a register that throws must not
                    // take the terminal switcher and the queue link down with it.
                    element: <RouteContent />,
                    errorElement: <RouteErrorFallback />,
                    children: [
                      // Organization-only: this is where "Switch terminal" lives, so
                      // requiring a working terminal to reach it would strand a device
                      // whose terminal is exactly what broke.
                      { path: 'device', element: <Device /> },
                      {
                        // Eligibility BEFORE scope. A role that cannot sell is moved to
                        // its own landing here, so typing /pos never marches a waiter
                        // through terminal setup to reach a register the server refuses.
                        element: <RequireSell />,
                        children: [
                          {
                            element: <RequireBranch />,
                            children: [
                              { path: 'queue', element: <Queue /> },
                              {
                                path: 'queue/:id',
                                element: <RetailOrderDetail />,
                              },
                              {
                                path: 'queue/:id/edit',
                                element: <OrderEdit />,
                              },
                              {
                                // Money. An order is created against a terminal and the
                                // day session belongs to one, so these two — and only
                                // these two — send the user to /setup and back when
                                // there isn't one.
                                element: <RequireTerminal />,
                                children: [
                                  { index: true, element: <Register /> },
                                  { path: 'today', element: <Today /> },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                      {
                        // Stock operators may not be cashiers. Physical receiving
                        // needs a warehouse-assigned terminal, but never borrows the
                        // register's `sell` permission.
                        element: <RequireStockOperations />,
                        children: [
                          {
                            element: <RequireTerminal />,
                            children: [{ path: 'stock', element: <Stock /> }],
                          },
                        ],
                      },
                      {
                        // Ungated catalog read (GET /products), so every role keeps it
                        // — and it is where a waiter with no floor lands.
                        element: <RequireBranch />,
                        children: [{ path: 'menu', element: <Menu /> }],
                      },
                      {
                        /*
                          Correcting an order, from the queue OR the floor.

                          Branch-only, matching the two screens that send people here.
                          Deliberately NOT under RequireTerminal: /pos/tables has no
                          terminal requirement, so demanding one would let a waiter
                          reach a tab and then strand the correction behind device
                          setup. Not under RequireSell either — `orders.edit` is its own
                          capability and the route checks it, rather than borrowing the
                          gate for taking money.
                        */
                        element: <RequireBranch />,
                        children: [{ path: 'orders/:orderId/edit', element: <OrderEdit /> }],
                      },
                      {
                        // Restaurant fast-follow. The route is no longer registered for
                        // every organization type: hiding the nav entry left
                        // /pos/tables reachable by address bar from an ecommerce org.
                        // Eligibility first, so a non-restaurant org is turned away
                        // without being asked for a branch it will not get to use.
                        // Then branch-only — a waiter serves tables on a device that
                        // was never nominated a till.
                        element: <RequireRestaurant />,
                        children: [
                          {
                            element: <RequireBranch />,
                            children: [{ path: 'tables', element: <Tables /> }],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: '/',
            element: (
              <GroupBoundary fallback={<ShellSkeleton />}>
                {/*
                  `desk`: unsized fields, selects and buttons render at 36px and
                  no button glows, here and in every dialog, sheet and menu a
                  page portals out of this tree (owner, 2026-10-09). Settings is
                  nested under this route, so it inherits the register; the POS
                  routes are siblings and keep `touch`.
                */}
                <ControlRegisterProvider value="desk">
                  <OfficeShell />
                </ControlRegisterProvider>
              </GroupBoundary>
            ),
            children: [
              {
                // Like POS, lifecycle handling belongs inside the shell: the
                // right-hand panel becomes one suspension notice and the page
                // below it is unmounted, so its cards cannot emit duplicate errors.
                element: <RequireOrganizationAccess />,
                children: [
                  {
                    // Every office page hangs off the content boundary, not off the shell.
                    // A throw in /products now replaces the content area only — the sidebar,
                    // the topbar and both switchers stay mounted and usable.
                    element: <RouteContent />,
                    errorElement: <RouteErrorFallback />,
                    children: [
                      { index: true, element: <OfficeHome /> },
                      { path: 'menu', element: <OfficeMenuHub /> },
                      { path: 'assistant', element: <OfficeAssistant /> },
                      {
                        element: <RequireOnlineStore />,
                        children: [{ path: 'online-store', element: <OfficeOnlineStore /> }],
                      },
                      { path: 'orders', element: <OfficeOrders /> },
                      { path: 'orders/checkouts', element: <OfficeCheckoutHistory /> },
                      { path: 'orders/checkouts/:checkoutId', element: <OfficeCheckoutHistory /> },
                      { path: 'orders/requests', element: <Navigate to="/orders" replace /> },
                      { path: 'orders/new', element: <OfficeOrderCreate /> },
                      { path: 'orders/drafts', element: <Navigate to="/orders" replace /> },
                      { path: 'orders/drafts/:draftId', element: <OfficeOrderCreate /> },
                      { path: 'orders/:id', element: <OfficeOrderDetail /> },
                      { path: 'orders/:id/edit', element: <OfficeOrderEdit /> },
                      { path: 'products', element: <OfficeProducts /> },
                      {
                        element: <RequireReviews />,
                        children: [
                          { path: 'reviews', element: <OfficeReviews /> },
                          { path: 'reviews/:publicId', element: <OfficeReviewDetail /> },
                        ],
                      },
                      {
                        element: <RequireCollections />,
                        children: [
                          { path: 'collections', element: <OfficeCollections /> },
                          { path: 'collections/new', element: <OfficeCollectionEditor /> },
                          { path: 'collections/:id/edit', element: <OfficeCollectionEditor /> },
                        ],
                      },
                      { path: 'products/new', element: <OfficeProductEditor /> },
                      { path: 'products/:id/edit', element: <OfficeProductEditor /> },
                      {
                        element: <RequireOperationalCategories />,
                        children: [
                          { path: 'categories', element: <OfficeTaxonomy kind="category" /> },
                        ],
                      },
                      { path: 'brands', element: <OfficeTaxonomy kind="brand" /> },
                      { path: 'media', element: <OfficeMedia /> },
                      { path: 'blogs', element: <OfficeBlogs /> },
                      { path: 'blogs/new', element: <OfficeBlogEditor /> },
                      { path: 'blogs/:id/edit', element: <OfficeBlogEditor /> },
                      { path: 'content/menus', element: <OfficeMenus /> },
                      { path: 'content/menus/new', element: <OfficeMenuEditor /> },
                      { path: 'content/menus/:id/edit', element: <OfficeMenuEditor /> },
                      { path: 'pages', element: <OfficePages /> },
                      { path: 'pages/new', element: <OfficePageEditor /> },
                      { path: 'pages/:id/edit', element: <OfficePageEditor /> },
                      // Same rule as the POS floor: administering a floor plan an
                      // ecommerce organization does not have is not a screen, so the
                      // route is gated rather than merely unlinked.
                      {
                        element: <RequireRestaurant />,
                        children: [{ path: 'tables', element: <OfficeFloorPlan /> }],
                      },
                      { path: 'inventory', element: <OfficeInventory /> },
                      { path: 'inventory/history', element: <OfficeStockMovements /> },
                      { path: 'purchasing', element: <Navigate to="/purchase-orders" replace /> },
                      { path: 'purchase-orders', element: <OfficePurchaseOrders /> },
                      { path: 'purchase-orders/new', element: <OfficePurchaseOrderCreate /> },
                      { path: 'purchase-orders/:id', element: <OfficePurchaseOrderDetail /> },
                      { path: 'suppliers', element: <OfficeSuppliers /> },
                      { path: 'suppliers/:id', element: <OfficeSupplierDetail /> },
                      { path: 'repairs', element: <OfficeRepairs /> },
                      { path: 'receipts', element: <OfficeReceipts /> },
                      { path: 'inventory-counts', element: <OfficeInventoryCounts /> },
                      { path: 'transfers', element: <OfficeTransfers /> },
                      { path: 'transfers/new', element: <OfficeTransferCreate /> },
                      { path: 'transfers/:id', element: <OfficeTransferDetail /> },
                      { path: 'inventory-transfers/*', element: <LegacyTransferRedirect /> },
                      { path: 'inventory-returns', element: <OfficeInventoryReturns /> },
                      { path: 'inventory-insights', element: <OfficeInventoryInsights /> },
                      // The old all-in-one "advanced purchasing" screen is gone; its pieces
                      // now live on the supplier page, the receipt, Repairs and Price lists.
                      {
                        path: 'purchasing-advanced',
                        element: <Navigate to="/suppliers" replace />,
                      },
                      /*
                  The mirror of the two RequireRestaurant groups around this one.

                  Shipping zones and warehouses are the PARCEL model. A restaurant
                  register never reads either — `deliveryPricingFor` answers
                  'area' for it (src/types/checkout.ts) — so a restaurant manager
                  who found these could build zones with real rates on them and
                  nothing would ever charge one. Inert money configuration that
                  reports success is worse than a screen that is simply not there.

                  Gated, not merely unlinked, for exactly the reason recorded
                  above /tables: hiding the nav entry leaves the address bar open,
                  which is how the floor plan stayed reachable from an ecommerce
                  org for a whole release. Retail and neutral are unaffected —
                  both guards ask `hasFeature`, which answers yes for both
                  (src/lib/business-profile.ts:129-131).
                */
                      /*
                    THE SETTINGS SURFACE — a pathless layout, so not one URL moves.

                    These eight screens left the main rail (owner, 2026-08-20:
                    "setting ka separate chaiya") and share a sub-rail now. They
                    are TOP-LEVEL paths and have always been: /shipping,
                    /store-settings, /themes. Re-parenting them under /settings
                    would have been the obvious way to give them a common chrome
                    and would have broken every bookmark to all eight. A pathless
                    route gives them the chrome and matches exactly what it
                    matched before.

                    The three access guards below are UNCHANGED and stay INSIDE
                    this layout rather than around it: whether a screen exists is
                    a different question from what chrome it wears, and hoisting
                    them would have made the whole settings surface restaurant- or
                    retail-only.
                  */
                      {
                        element: <SettingsShell />,
                        children: [
                          { path: 'billing', element: <BillingRequired /> },
                          { path: 'settings/*', element: <OfficeSettings /> },
                          { path: 'store-settings', element: <OfficeStoreSettings /> },
                          { path: 'checkout-settings', element: <OfficeCheckoutSettings /> },
                          { path: 'payment-methods', element: <OfficePaymentMethods /> },
                          { path: 'themes', element: <OfficeThemes /> },
                          { path: 'themes/:themeId/code', element: <OfficeThemeCode /> },
                          { path: 'sessions', element: <OfficeSessions /> },
                          {
                            element: <RequireWarehouses />,
                            children: [{ path: 'warehouses', element: <OfficeWarehouses /> }],
                          },
                          {
                            element: <RequireShippingZones />,
                            children: [
                              { path: 'shipping', element: <OfficeShipping /> },
                              { path: 'shipping/new', element: <OfficeShippingZoneEditor /> },
                              { path: 'shipping/:id/edit', element: <OfficeShippingZoneEditor /> },
                            ],
                          },
                          {
                            element: <RequireRestaurant />,
                            children: [
                              { path: 'delivery-areas', element: <OfficeAreas /> },
                              { path: 'branch-settings', element: <OfficeBranchSettings /> },
                            ],
                          },
                        ],
                      },
                      /*
                  SESSIONS IS NOT A RESTAURANT SCREEN, AND WAS ONLY EVER HERE BY
                  INHERITANCE.

                  A shift belongs to a TILL, not to a floor: `shift` is open to
                  every mode (src/lib/business-profile.ts) and pos-api's day-session
                  routes carry no organization-type condition. A retail counter
                  genuinely opens one, takes cash against it and closes it — so
                  gating this route on RequireRestaurant meant a shop could take
                  money on a till and then have nowhere to read the record of it.
                  Its Z-report is the shift handover document.

                  The page was already written FOR retail — it passes the
                  organization type into ZReport so a shop reads Walk-in rather
                  than Pickup (office/sessions/SessionsPage.tsx) — which is the
                  clearest sign the gate was never a decision about this screen.
                */
                      { path: 'promotions', element: <OfficePromotions /> },
                      { path: 'promotions/new', element: <OfficePromotionEditor /> },
                      { path: 'promotions/:id/edit', element: <OfficePromotionEditor /> },
                      { path: 'coupons', element: <Navigate to="/promotions" replace /> },
                      { path: 'customers', element: <OfficeCustomers /> },
                      { path: 'customers/subscribers', element: <OfficeSubscribers /> },
                      { path: 'customers/segments', element: <OfficeSegments /> },
                      { path: 'customers/segments/:id', element: <OfficeSegments /> },
                      { path: 'customers/:id', element: <OfficeCustomerDetail /> },
                      { path: 'forms', element: <OfficeForms /> },
                      { path: 'forms/:id', element: <OfficeForms /> },
                      { path: 'analytics', element: <OfficeAnalytics /> },
                      // Every custom app uses this one host route. Installing a new
                      // app changes signed metadata and never this route table.
                      { path: 'apps', element: <AppStore /> },
                      { path: 'apps/:appKey', element: <AppDetails /> },
                      { path: 'apps/:appKey/run/*', element: <InstalledApp /> },
                      // Owner-only in pos-api. The pages redirect a known non-owner
                      // role themselves rather than being gated here, so an unknown
                      // role (a pre-roles backend) still reaches the server and gets
                      // its answer from the only authority there is.
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];

export const router = createBrowserRouter(routes);
